
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import Input from '../../../components/base/Input';
import Textarea from '../../../components/base/Textarea';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollAnimation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      message: formData.get('message')
    };

    try {
      const response = await fetch('https://readdy.ai/api/form/d3mlhq2i723b7tba328g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(data as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        (e.target as HTMLFormElement).reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  return (
    <section 
      id="contact" 
      ref={ref}
      className={`py-16 md:py-24 bg-white transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 sm:mb-12">
          <img 
            src="https://public.readdy.ai/ai/img_res/3e0c73dd005b527d96fe4ad657bddf31.jpg"
            alt={t('contact.imageAlt', 'Entrada de iglesia acogedora')}
            className="w-full h-40 sm:h-48 md:h-64 object-cover object-top rounded-lg shadow-lg"
          />
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
          <div className="order-2 lg:order-1">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight mb-6 sm:mb-8 text-center lg:text-left">
              {t('contact.title')}
            </h2>
            
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-lg sm:text-xl text-neutral-700"></i>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-neutral-900 mb-1 text-sm sm:text-base">{t('contact.addressLabel')}</h3>
                  <p className="text-neutral-600 text-sm sm:text-base leading-relaxed">{t('contact.address') || t('contact.comingSoon')}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-lg sm:text-xl text-neutral-700"></i>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-neutral-900 mb-1 text-sm sm:text-base">Email</h3>
                  <p className="text-neutral-600 text-sm sm:text-base break-all">{t('contact.email')}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-lg sm:text-xl text-neutral-700"></i>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-neutral-900 mb-1 text-sm sm:text-base">{t('contact.phoneLabel')}</h3>
                  <p className="text-neutral-600 text-sm sm:text-base">{t('contact.phone') || t('contact.comingSoon')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-lg sm:text-xl text-neutral-700"></i>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-neutral-900 mb-2 text-sm sm:text-base">{t('contact.scheduleLabel')}</h3>
                  <div className="space-y-1 text-neutral-600 text-sm sm:text-base">
                    <p>{t('contact.comingSoon')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="order-1 lg:order-2 bg-neutral-50 border border-neutral-200 rounded-xl lg:rounded-2xl p-4 sm:p-6 lg:p-8">
            <h3 className="text-lg sm:text-xl font-semibold text-neutral-900 mb-4 sm:mb-6 text-center lg:text-left">
              {t('contact.form.title')}
            </h3>
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <Input
                name="name"
                label={t('contact.form.name')}
                placeholder={t('contact.form.namePlaceholder')}
                required
                className="text-sm sm:text-base"
              />
              
              <Input
                name="email"
                type="email"
                label={t('contact.form.email')}
                placeholder={t('contact.form.emailPlaceholder')}
                required
                className="text-sm sm:text-base"
              />
              
              <Textarea
                name="message"
                label={t('contact.form.message')}
                placeholder={t('contact.form.messagePlaceholder')}
                required
                maxLength={500}
                className="text-sm sm:text-base"
                rows={4}
              />
              
              <Button 
                type="submit" 
                className="w-full whitespace-nowrap text-sm sm:text-base"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('contact.form.sending') : t('contact.form.send')}
              </Button>
              
              {submitStatus === 'success' && (
                <p className="text-green-600 text-sm text-center">{t('contact.form.successMessage')}</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-600 text-sm text-center">{t('contact.form.errorMessage')}</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
